import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Sparkles, RotateCcw } from "lucide-react";
import { ASSETS } from "@/data/content";
import { readSSE, aiErrorMessage } from "@/lib/sse";
import { renderRich } from "@/lib/rich.jsx";

import { API } from "@/lib/api";

const WELCOME = {
  role: "assistant",
  content:
    "Γεια σας! Είμαι ο ψηφιακός βοηθός της CRESTIA. Ρωτήστε με οτιδήποτε για τις υπηρεσίες, τη μεθοδολογία ή το πώς μπορούμε να βοηθήσουμε την επιχείρησή σας.",
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(crypto.randomUUID());
  const listRef = useRef(null);
  const requestRef = useRef(null);
  useEffect(() => () => requestRef.current?.abort(), []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (retry = null) => {
    const text = retry ? retry.text : input.trim();
    if (!text || requestRef.current) return;
    const turnId = retry ? retry.turnId : crypto.randomUUID();
    const controller = new AbortController();
    requestRef.current = controller;
    if (retry) {
      setMessages((m) => m.map((item) => item.turnId === turnId && item.role === "assistant"
        ? { ...item, content: "", error: null, pending: true } : item));
    } else {
      setInput("");
      setMessages((m) => [...m, { role: "user", content: text, turnId },
        { role: "assistant", content: "", turnId, requestText: text, pending: true }]);
    }
    setLoading(true);
    const update = (fn) => setMessages((m) => m.map((item) =>
      item.turnId === turnId && item.role === "assistant" ? fn(item) : item));
    try {
      await readSSE(
        `${API}/ai/chat`,
        { session_id: sessionRef.current, request_id: turnId, message: text },
        (delta) => update((item) => ({ ...item, content: item.content + delta })),
        { signal: controller.signal }
      );
      update((item) => ({ ...item, pending: false }));
    } catch (e) {
      if (!controller.signal.aborted) {
        update((item) => ({ ...item, pending: false, error: aiErrorMessage(e) }));
      }
    } finally {
      requestRef.current = null;
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[400px] bg-white border border-[#12233F]/10 shadow-[0_30px_60px_-15px_rgba(18,35,63,0.3)] flex flex-col overflow-hidden"
            style={{ height: "min(560px, calc(100vh - 8rem))" }}
          >
            <div className="flex items-center gap-3 bg-[#12233F] px-5 py-4">
              <span className="flex items-center justify-center h-9 w-9 bg-[#F7F5F0] p-1">
                <img src={ASSETS.logo} alt="CRESTIA" className="h-full w-auto object-contain" />
              </span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#F7F5F0]">AI Βοηθός CRESTIA</p>
                <p className="text-[11px] text-slate-300/80">Απαντά άμεσα, στα ελληνικά</p>
              </div>
              <button
                data-testid="chat-close-button"
                onClick={() => setOpen(false)}
                className="text-slate-300 hover:text-white transition-colors p-1"
                aria-label="Κλείσιμο συνομιλίας"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-[#F7F5F0]" data-testid="chat-messages" role="log" aria-live="polite" aria-busy={loading}>
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-message-${m.role}-${i}`}
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#12233F] text-[#F7F5F0]"
                        : "bg-white border border-[#12233F]/10 text-[#2C3E55]"
                    }`}
                  >
                    {m.content && <div className="whitespace-pre-wrap">{renderRich(m.content)}</div>}
                    {m.pending && (
                      <span role="status" className="inline-flex items-center gap-2 text-[#64748B]">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> {m.content ? "Συντάσσει την απάντηση..." : "Πληκτρολογεί..."}
                      </span>
                    )}
                    {m.error && (
                      <div className="space-y-2" data-testid="chat-error" role="alert">
                        {m.content && <p className="text-xs">Η παραπάνω απάντηση είναι ημιτελής.</p>}
                        <p>{m.error}</p>
                        {i === messages.length - 1 && (
                          <button type="button" data-testid="chat-retry-button" disabled={loading}
                            onClick={() => send({ text: m.requestText, turnId: m.turnId })}
                            className="inline-flex min-h-[44px] items-center gap-2 text-[#967536] font-semibold disabled:opacity-50">
                            <RotateCcw className="h-4 w-4" /> Δοκιμή ξανά
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[#12233F]/10 bg-white p-3 flex items-center gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.nativeEvent.isComposing && send()}
                maxLength={1500}
                aria-label="Η ερώτησή σας προς τον AI βοηθό"
                placeholder="Γράψτε την ερώτησή σας..."
                className="flex-1 bg-[#F7F5F0] border border-[#12233F]/10 px-4 py-2.5 text-sm text-[#12233F] placeholder:text-[#64748B]/60 focus:outline-none focus:border-[#B08D46]"
              />
              <button
                data-testid="chat-send-button"
                onClick={() => send()}
                disabled={loading || !input.trim()}
                className="flex items-center justify-center h-10 w-10 bg-[#B08D46] text-white hover:bg-[#967536] disabled:opacity-50 transition-colors"
                aria-label="Αποστολή μηνύματος"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid="chat-toggle-button"
        onClick={() => setOpen((v) => !v)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-5 right-4 sm:right-6 z-50 flex items-center gap-2 bg-[#12233F] text-[#F7F5F0] pl-4 pr-5 py-3.5 shadow-[0_15px_35px_-10px_rgba(18,35,63,0.45)] border border-[#B08D46]/40"
        aria-label={open ? "Κλείσιμο AI βοηθού" : "Άνοιγμα AI βοηθού"}
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5 text-[#D4B870]" />}
        <span className="text-sm font-semibold">{open ? "Κλείσιμο" : "Ρωτήστε μας"}</span>
        {!open && <Sparkles className="h-3.5 w-3.5 text-[#D4B870]" />}
      </motion.button>
    </>
  );
};
